import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from '../hooks/use-toast';
import api from '../utils/api';
import { CheckCircle2, XCircle, Camera, ArrowLeft } from 'lucide-react';

const QRScannerPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [event, setEvent] = useState(null);
  const [eventLoading, setEventLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/scan/${eventId}`)
      .then((res) => setEvent(res.data))
      .catch(() => setEvent(null))
      .finally(() => setEventLoading(false));
  }, [eventId]);

  const handleScanSuccess = React.useCallback(async (qrCode) => {
    try {
      const res = await api.post(`/scan/${eventId}/verify`, { qrCode });
      setScanResult({
        success: true,
        message: res.data.message,
        guest: res.data.guest,
      });
      toast({
        title: 'Check-in Successful!',
        description: `${res.data.guest.name} has been marked present`,
      });
    } catch (err) {
      const guest = err?.error?.guest || null;
      setScanResult({
        success: false,
        message: err?.error?.message || 'Invalid QR code or guest not found',
        guest,
      });
      toast({
        title: err?.error?.code === 'ALREADY_ATTENDED' ? 'Already Checked In' : 'Invalid QR Code',
        description: err?.error?.message || 'This QR code is not valid for this event',
        variant: 'destructive',
      });
    }
  }, [eventId]);

  useEffect(() => {
    if (scanning && !scannerRef.current) {
      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        {
          fps: 10,
          qrbox: { width: 300, height: 300 },
          aspectRatio: 1.0,
        },
        false
      );

      scanner.render(
        (decodedText) => {
          // QR Code scanned successfully
          handleScanSuccess(decodedText);
          scanner.clear();
          scannerRef.current = null;
          setScanning(false);
        },
        (error) => {
          // Scan error (can be ignored for continuous scanning)
          console.log(error);
        }
      );

      scannerRef.current = scanner;
    }

    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
        scannerRef.current = null;
      }
    };
  }, [scanning, handleScanSuccess]);

  const startScanning = () => {
    setScanResult(null);
    setScanning(true);
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Event not found</h2>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <Card className="p-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="text-center space-y-2">
            <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center mx-auto">
              <Camera className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold">{event.name}</h1>
            <p className="text-gray-600">
              {new Date(event.date).toLocaleDateString()} at {event.venue}
            </p>
          </div>
        </Card>

        {/* Scanner */}
        {!scanning && !scanResult && (
          <Card className="p-8 text-center space-y-4">
            <Camera className="w-20 h-20 mx-auto text-gray-300" />
            <h2 className="text-xl font-semibold">Scan QR Code</h2>
            <p className="text-gray-600">
              Click the button below to start scanning attendee QR codes
            </p>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={startScanning}
            >
              <Camera className="w-4 h-4 mr-2" />
              Start Scanning
            </Button>
          </Card>
        )}

        {/* Scanner View */}
        {scanning && (
          <Card className="p-6">
            <div id="qr-reader" className="w-full"></div>
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => {
                if (scannerRef.current) {
                  scannerRef.current.clear();
                  scannerRef.current = null;
                }
                setScanning(false);
              }}
            >
              Cancel Scanning
            </Button>
          </Card>
        )}

        {/* Scan Result */}
        {scanResult && (
          <Card className="p-8 text-center space-y-6">
            {scanResult.success ? (
              <div className="space-y-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-green-600">Check-in Successful!</h2>
                {scanResult.guest && (
                  <div className="space-y-2">
                    <p className="text-xl font-semibold">{scanResult.guest.name}</p>
                    <p className="text-gray-600">{scanResult.guest.company}</p>
                    <p className="text-sm text-gray-500">{scanResult.guest.email}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-red-600">Check-in Failed</h2>
                <p className="text-gray-600">{scanResult.message}</p>
                {scanResult.guest && (
                  <div className="space-y-2">
                    <p className="text-xl font-semibold">{scanResult.guest.name}</p>
                    <p className="text-sm text-gray-500">Already attended</p>
                  </div>
                )}
              </div>
            )}
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              onClick={startScanning}
            >
              Scan Next
            </Button>
          </Card>
        )}

        {/* Instructions */}
        <Card className="p-6">
          <h3 className="font-semibold mb-3">Instructions:</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-orange-500 font-bold">1.</span>
              <span>Click "Start Scanning" to activate the camera</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 font-bold">2.</span>
              <span>Point your camera at the attendee's QR code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 font-bold">3.</span>
              <span>The system will automatically detect and verify the QR code</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 font-bold">4.</span>
              <span>Attendance will be marked automatically upon successful scan</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default QRScannerPage;